import { useGetWireRecipientsList } from '@/hooks/requests/useWireRecipients';
import { useState } from 'react';
import { RecipientStatus } from '@/types/wireRecipients';
import { SVTableRow } from '@/types/common';
import { titleCase } from '@/utils/stringCaser';
import WireRecipientManageMenu from './WireRecipientManageMenu';
import { SVLoader, SVTable } from '@/components/core';

type WireRecipientsStatusTableProps = {
  customerId: string;
  status: RecipientStatus;
};

export default function WireRecipientsStatusTable({
  customerId,
  status,
}: WireRecipientsStatusTableProps) {
  const isActive = status === 'active';
  const [page, setPage] = useState(0);
  const { wireRecipients, isWireRecipientsPending, totalRecordCount } =
    useGetWireRecipientsList({
      consumer_id: customerId,
      status,
      page,
    });

  if (isWireRecipientsPending) {
    return <SVLoader />;
  }

  const rows: SVTableRow[] = wireRecipients.map(recipient => {
    const {
      uuid,
      accountName,
      recipientNickName,
      bankName,
      status,
      activatedAt,
      deletedAt,
    } = recipient;

    return {
      key: uuid,
      cells: [
        {
          content: accountName,
          type: 'text',
          key: `name${uuid}`,
        },
        {
          content: recipientNickName,
          type: 'text',
          key: `nickname${uuid}`,
        },
        {
          content: bankName,
          type: 'text',
          key: `bankName${uuid}`,
        },
        {
          content: titleCase(status),
          type: 'text',
          key: `status${uuid}`,
        },
        {
          content: isActive ? activatedAt : deletedAt,
          type: 'date',
          key: `date${uuid}`,
        },
        {
          content: (
            <WireRecipientManageMenu
              customerId={customerId}
              uuid={uuid}
              canDelete={isActive}
              name={recipientNickName}
              isWireRecipient
            />
          ),
          type: 'custom',
          key: `manage${uuid}`,
        },
      ],
    };
  });

  return (
    <SVTable
      ariaLabel={`${status} Wire Recipients`}
      headers={[
        { content: 'Name', key: 'name' },
        { content: 'Nickname', key: 'nickname' },
        { content: 'Bankname', key: 'bankname' },
        { content: 'Status', key: 'status' },
        { content: isActive ? 'Activated' : 'Deleted', key: 'date' },
        { content: '', key: 'manage' },
      ]}
      rows={rows}
      hasPagination
      page={page}
      setPage={setPage}
      totalRecordCount={totalRecordCount}
    />
  );
}
